// English — source of truth for all UI strings. Non-English bundles are stored in the
// backend translation store (Admin → Translations) and merged over these keys at runtime.
// Stable, namespaced dot-keys. Keep commercial facts out of here (they live in structured data).
import enClient from "./en_client";
import enPages from "./en_pages";
const en = {
  lang: {
    label: "Language",
    choose: "Choose language",
  },
  common: {
    getStarted: "Get Started",
    learnMore: "Learn more",
    viewDetails: "View details",
    browseAll: "Browse all",
    compare: "Compare",
    back: "Back",
    next: "Next",
    submit: "Submit",
    save: "Save changes",
    cancel: "Cancel",
    loading: "Loading…",
    retry: "Retry",
    from: "from",
    perYear: "/year",
    setup: "Set-up",
    annual: "Annual renewal",
    timeline: "Timeline",
    askAi: "Ask Uni AI",
    contact: "Contact",
    login: "Login",
    clientPortal: "Client Portal",
    signOut: "Sign out",
    dashboard: "Dashboard",
    adminConsole: "Admin console",
    whatsapp: "WhatsApp",
    all: "All",
  },
  nav: {
    home: "Home",
    whoWeAre: "Who We Are",
    companyFormation: "Company Formation",
    bankAccount: "Bank Account",
    moreServices: "More Services",
    partnerProgram: "Partner Program",
    regions: "Regions",
    jurisdictions: "jurisdictions",
    browseCountries: "Browse all countries",
    compareJurisdictions: "Compare jurisdictions",
    digitalEmi: "Digital & EMI accounts",
    traditionalBanks: "Traditional banks",
    paymentSolutions: "Payment Solutions",
    paymentGateway: "Payment Gateway Integration",
    paymentGatewayDesc: "Gateway selection, merchant onboarding & integration support",
    browseBanks: "Browse all banks",
    compareBanks: "Compare banks",
    viewAllServices: "View all services",
    allCountries: "All countries",
    allBanksCompare: "All banks & compare",
  },
  footer: {
    tagline: "Independent private corporate services provider assisting with company formation, banking introductions, accounting, tax and compliance across leading jurisdictions.",
    services: "Services",
    company: "Company",
    legal: "Legal",
    getInTouch: "Get in touch",
    about: "About us",
    contact: "Contact",
    partnership: "Partnership",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    cookies: "Cookie Policy",
    disclaimer: "Disclaimer",
    rights: "All rights reserved.",
    independentNotice: "UNICORP is an independent private corporate services provider. We are not a government agency, registry, bank or regulator.",
    blurb: "Expand beyond borders. We handle incorporation, banking and compliance across leading global jurisdictions so you can focus on building.",
    stayUpdated: "Stay updated",
    newsletterBody: "Occasional updates on jurisdictions, banking and compliance. No spam.",
    subscribed: "You're subscribed. Thank you.",
    emailPlaceholder: "Your email address",
    emailAria: "Email address",
    subscribe: "Subscribe",
    subscribeError: "Please enter a valid email address.",
    region: { northAmerica: "North America", europe: "Europe", asia: "Asia", caribbean: "Caribbean", africa: "Africa", oceania: "Oceania" },
    svc: { incorporation: "Company Incorporation", bankAccounts: "Corporate Bank Accounts", accounting: "Accounting & Tax", licensing: "Licensing & Regulatory", trusts: "Trusts & Foundations", einitin: "EIN & ITIN" },
    contactUs: "Contact us",
    legalLine: "An independent private corporate services provider — not a government agency, registry, bank or regulatory authority.",
    regNo: "Reg. No: {{v}}",
    regJurisdiction: "Registered jurisdiction: {{v}}",
    regOffice: "Registered office: {{v}}",
    refund: "Refund Policy",
    cookiePrefs: "Cookie preferences",
    support: "Support",
    slogan: "Expand Beyond Borders",
  },
  home: {
    heroKicker: "Global corporate services",
    heroTitle: "Expand Beyond Borders",
    heroTitle1: "Start, manage and grow",
    heroTitle2: "your international company.",
    heroSubtitle: "Company formation, banking assistance, accounting, compliance and corporate services across leading jurisdictions — transparent pricing, remote onboarding, managed end to end.",
    heroCtaPrimary: "Start a Company",
    heroCtaSecondary: "Ask Uni AI",
    heroCtaExplore: "Explore Jurisdictions",
    heroCtaExpert: "Talk to an Expert",
    trustPricing: "Transparent pricing",
    trustJurisdictions: "Global jurisdictions",
    trustRemote: "Remote onboarding",
    cardPricing: "All-inclusive pricing",
    cardNoFees: "No hidden fees, ever",
    trustedBy: "Trusted by founders worldwide",
    whyTitle: "Everything you need to go global",
    whySubtitle: "One partner for incorporation, banking and ongoing compliance.",
    ctaTitle: "Your next business move starts here.",
    ctaSubtitle: "Whether you're forming a company, opening a business account, structuring your business or building your digital platform — UNICORP helps you move forward.",
    journeyKicker: "Choose your journey",
    journeyStartTitle: "Start a Company",
    journeyStartBody: "Incorporate in the right jurisdiction.",
    journeyBankTitle: "Open a Business Account",
    journeyBankBody: "Banking application & introduction assistance.",
    journeyComplianceTitle: "Manage Compliance",
    journeyComplianceBody: "Accounting, tax filing and annual renewals.",
    journeySupportTitle: "Existing Client Support",
    journeySupportBody: "Track applications in your Client Portal.",
    trustSecuredTitle: "Secured payments",
    trustSecuredBody: "Encrypted checkout via Stripe",
    trustScopeTitle: "Transparent scope",
    trustScopeBody: "Requirements & pricing up front",
    trustSpecialistTitle: "A specialist per case",
    trustSpecialistBody: "One point of contact for your case",
    trustPrivateTitle: "Private & confidential",
    trustPrivateBody: "Documents handled securely",
    flowBuildLabel: "Build",
    flowBuildBody: "Form your company in the right jurisdiction.",
    flowStructureLabel: "Structure",
    flowStructureBody: "Foundations, trusts and holding vehicles.",
    flowBankLabel: "Bank",
    flowBankBody: "Open business & multi-currency accounts.",
    flowOperateLabel: "Operate",
    flowOperateBody: "Nominees, licensing and compliance.",
    flowGrowLabel: "Grow",
    flowGrowBody: "Digital products and global expansion.",
    solutionsKicker: "Global business solutions",
    solutionsTitle: "Everything it takes to go global — in one place.",
    solutionsBody: "From the first entity to a fully operating, growing business. One partner across every stage.",
    pillarFormationKicker: "Company Formation",
    pillarFormationTitle: "Build your company.",
    pillarFormationBody: "Global company formation across supported jurisdictions",
    pillarFormationFrom: "from {{price}} all-inclusive",
    pillarFormationBodyExtra: "Government & registry fees are included in the jurisdiction price; final eligibility depends on your structure, activity and residency.",
    pillarFormationCta: "Explore Company Formation",
    pillarFormationStart: "Start Formation",
    pillarBankingKicker: "Bank Account",
    pillarBankingTitle: "Then build your financial infrastructure.",
    pillarBankingBody: "Business banking and multi-currency solutions",
    pillarBankingBodyWith: "with {{count}} banking options we can introduce",
    pillarBankingBodyExtra: " — digital, EMI and traditional. Account approval is decided by each provider.",
    pillarBankingCta: "Explore Bank Accounts",
    pillarBankingOpen: "Open a Bank Account",
    coverageKicker: "Company formation & banking coverage",
    coverageJurisdictions: "Jurisdictions",
    coverageRegions: "Regions",
    coverageBankingOptions: "Banking options",
    coverageRegionsLabel: "Regions:",
    ecosystemKicker: "The wider ecosystem",
    ecosystemTitle: "Beyond incorporation.",
    ecosystemCta: "Explore More Services",
    studioKicker: "UNICORP Digital Studio",
    studioTitle1: "Build your",
    studioTitleAccent: "digital",
    studioTitle2: "business.",
    studioBody: "From idea to digital product. Tell us what you want to build — we'll help you understand the scope, budget and the best way to launch.",
    studioBuild: "Build My Project",
    studioEstimate: "Estimate My Budget",
    studioCapWebsite: "Website",
    studioCapAndroid: "Android App",
    studioCapIos: "iOS App",
    studioCapWebApp: "Web Application",
    studioCapSaas: "SaaS Platform",
    studioCapAi: "AI Solutions",
    studioCapSoftware: "Custom Software",
    aiKicker: "Your business concierge",
    aiTitle: "Not sure where to start? Ask Uni AI.",
    aiBody: "Tell Uni AI what you're trying to achieve — a jurisdiction, a structure, a bank account or a digital build — and get a clear, tailored path forward.",
    aiChatUser: "I want to open a company and a bank account for a crypto startup.",
    aiChatBot: "Great — a VASP-friendly jurisdiction plus an EMI account is a strong start. Want me to compare two options?",
    aiTyping: "Uni AI is typing…",
    processKicker: "How it works",
    processTitle: "A clear path, start to finish.",
    process1Title: "Tell us what you need",
    process1Body: "Share your goal — a company, an account, a structure or a product.",
    process2Title: "Explore the right option",
    process2Body: "We match you to the ideal jurisdiction, service or build approach.",
    process3Title: "Configure your solution",
    process3Body: "Transparent scope and pricing, tailored to your case.",
    process4Title: "Complete securely",
    process4Body: "Finish onboarding, KYC and payment — remotely.",
    process5Title: "Track in your portal",
    process5Body: "Follow every step to delivery from your Client Portal.",
  },
  contact: {
    kicker: "Contact",
    title: "Let's talk about your expansion.",
    subtitle: "Tell us where you want to grow and we'll map the incorporation, banking and compliance path for you. A specialist typically replies within one business day.",
    formTitle: "Send us a message",
    formRequiredNote: "All fields marked with * are required.",
    labelName: "Full name *",
    labelEmail: "Email *",
    labelPhone: "Phone / WhatsApp",
    labelInterest: "I'm interested in",
    labelResidence: "Country of residence",
    labelJurisdiction: "Preferred jurisdiction",
    labelHelp: "How can we help? *",
    phonePlaceholder: "+852 …",
    residencePlaceholder: "e.g. United Kingdom",
    notSure: "Not sure",
    consentPre: "I agree to the",
    consentPost: "and consent to UNICORP contacting me about my enquiry. *",
    errorRateLimit: "Too many submissions — please try again later or WhatsApp us.",
    errorGeneric: "Something went wrong. Please try again or WhatsApp us.",
    infoCall: "Call / Mobile",
    infoEmail: "Email us",
    infoOffice: "Head office",
    headOffice: "Head office",
    headOfficeCity: "Hong Kong",
    chatInstantly: "Chat instantly",
    whatsappTeam: "WhatsApp our team",
    hours: "Mon–Fri, 9:00–18:00 HKT · Replies within 1 business day",
    legalTitle: "Registered business details",
    legalEntity: "Legal entity",
    legalRegNumber: "Registration number",
    legalJurisdiction: "Registered jurisdiction",
    legalOffice: "Registered office",
    legalEmail: "Email",
    legalWebsite: "Website",
  },
  wizard: {
    stepOrder: "Order",
    stepCompany: "Company",
    stepDirectors: "Directors",
    stepDocuments: "Documents",
    stepReview: "Review",
    stepApplicant: "Applicant",
    orderTitleBank: "Your bank account",
    orderTitleInc: "Build your order",
    orderSubtitleBank: "Order a corporate account on its own — no incorporation required.",
    orderSubtitleInc: "Choose a country, add a bank and any extras.",
    loadingBank: "Loading bank…",
    jurisdiction: "jurisdiction",
    jurisdictions: "jurisdictions",
    alsoIncorporate: "Also incorporate a company",
    save10: "Save 10%",
    selectCountryIncorporate: "Select country to incorporate",
    bundleSaving: "You save {{amount}} by bundling incorporation + bank together.",
    countryOfIncorporation: "Country of incorporation",
    selectCountry: "Select country",
    setupThen: "Set-up {{setup}} · then {{annual}}/year",
    addBankAccount: "Add a corporate bank account",
    selectBank: "Select a bank",
    optionalAddons: "Optional add-ons",
    companyTitleBank: "Account holder details",
    companyTitleInc: "Company details",
    companySubtitleBank: "The company that will hold this bank account. Already incorporated? Just enter its name.",
    labelCompanyNameBank: "Company / account holder name",
    labelCompanyNameInc: "Proposed company name",
    companyNamePlaceholder: "e.g. Northbeam Holdings",
    labelEntityType: "Entity type",
    entityTypeOptional: "(optional)",
    selectType: "Select type",
    labelBusinessActivity: "Business activity",
    businessActivityPlaceholder: "Briefly describe your business…",
    directorsTitle: "Directors & shareholders",
    person: "Person {{n}}",
    emailOptional: "Email (optional)",
    nationality: "Nationality",
    ownership: "Ownership %",
    addPerson: "Add another person",
    documentsTitle: "Upload documents",
    documentsSubtitle: "Passport / ID and proof of address. Optional now.",
    uploading: "Uploading…",
    clickToUpload: "Click to upload",
    reviewTitle: "Review & checkout",
    yourName: "Your name",
    email: "Email",
    phoneOptional: "Phone (optional)",
    securedStripe: "Secured by Stripe. Test card 4242 4242 4242 4242.",
    orderSummary: "Order summary",
    loadingOrder: "Loading your order…",
    selectCountryToBegin: "Select a country to begin.",
    totalDueToday: "Total due today",
    renewalPerYear: "Renewal {{amount}}/year",
    continue: "Continue",
    requestQuotation: "Request quotation",
    redirecting: "Redirecting…",
    pay: "Pay {{amount}}",
    lineCompanyFormation: "{{name}} — Company Formation",
    lineBankAccount: "Bank Account — {{name}}",
    lineCorporateBankAccount: "Corporate Bank Account — {{name}}",
    lineBundleSaving: "Bundle saving (10% off)",
    uploaded: "Uploaded.",
    uploadFailed: "Upload failed.",
    errCompanyEntity: "Company name and entity type are required.",
    errAccountHolder: "Please enter the company / account holder name.",
    errDirector: "Add at least one director.",
    errContact: "Provide your contact name and email.",
    errCheckout: "Could not start checkout.",
    billedBy: "Billed by",
    regNo: "Reg No.",
  },
  forms: {
    name: "Full name",
    email: "Email",
    phone: "Phone",
    company: "Company",
    country: "Country",
    address: "Address",
    message: "Message",
    service: "Service of interest",
    selectCountry: "Select country",
    selectService: "Select a service",
    optional: "optional",
    namePlaceholder: "Your name",
    emailPlaceholder: "you@company.com",
    messagePlaceholder: "Tell us about your business and what you need…",
    consent: "I agree to the processing of my data in line with the Privacy Policy.",
    sending: "Sending…",
    send: "Send message",
    successTitle: "Message sent",
    successBody: "Thank you — our team will be in touch shortly.",
    errorGeneric: "Something went wrong. Please try again.",
    required: "This field is required.",
    invalidEmail: "Please enter a valid email address.",
    selectCountryError: "Please select a country.",
  },
  auth: {
    loginTitle: "Welcome back",
    loginSubtitle: "Sign in to start an application and track your service requests in the client portal.",
    continueGoogle: "Continue with Google",
    loginDisclaimer: "By continuing you agree to our Terms and Privacy Policy.",
    signingIn: "Signing you in…",
    backHome: "Back home",
    welcomeTitle: "Welcome to UNICORP",
    secureNote: "Secure sign-in. We never store your password.",
    sideTitle: "Expand beyond borders. Thrive worldwide.",
    sideBlurb: "Sign in to incorporate, open corporate accounts and track your service requests — all in one secure client portal.",
  },
  portal: {
    overview: "Overview",
    orders: "Orders",
    profile: "Profile",
    services: "Services",
    aiAdvisor: "AI Advisor",
    welcome: "Welcome back",
    statTotal: "Total orders",
    statActive: "In progress",
    statCompleted: "Completed",
    statDue: "Payment due",
    recentOrders: "Your orders",
    noOrders: "No orders yet",
    noOrdersBody: "Start your first application to track it here.",
    newOrder: "New order",
    ordersSubtitle: "All your applications and their progress.",
    reference: "Reference",
    workStatus: "Work status",
    paymentStatus: "Payment status",
    nextAction: "Next action",
    ordersError: "Couldn't load your orders",
    profileSubtitle: "Manage your contact details.",
    emailReadonly: "Your sign-in email can't be changed here.",
    verified: "Verified",
    profileSaved: "Profile updated.",
    profileError: "Couldn't save your profile. Please try again.",
    statusPendingPayment: "Awaiting payment",
    statusInProgress: "In progress",
    statusCompleted: "Completed",
    statusPaid: "Paid",
    statusUnpaid: "Unpaid",
    statusFailed: "Payment failed",
  },
  checkout: {
    payNow: "Pay now",
    processing: "Processing…",
    orderSummary: "Order summary",
    total: "Total",
    renewal: "Renewal {{amount}}/year",
    successTitle: "Payment confirmed",
    successBody: "Thank you — your request is now being processed by our specialists. You can track every step from your dashboard.",
    goToDashboard: "Go to dashboard",
    cancelTitle: "Payment cancelled",
    cancelBody: "No charge was made. You can resume your order whenever you're ready.",
    checking: "Confirming your payment…",
    pending: "Your payment is still processing. We'll update your order as soon as it clears.",
  },
  cookie: {
    title: "We use cookies",
    body: "We use essential cookies to keep the site working and to remember your preferences. See our",
    privacyPolicy: "Privacy Policy",
    accept: "Accept",
    necessaryOnly: "Necessary only",
  },
  notFound: {
    title: "Page not found",
    body: "The page you're looking for doesn't exist or has moved.",
    home: "Back to home",
  },
  disclaimer: {
    text: "UNICORP is an independent private corporate services provider. We are not a government agency, registry, bank or regulator. Government and registry fees are set by the relevant authority. Banking approval is decided solely by each bank or provider. Processing timelines are estimates and may vary.",
    compliance: "Requirements may change and can depend on business activity, ownership, tax residency and applicable regulation.",
    noteTitle: "{{name}} is an independent private corporate services provider.",
    noteBody: "We are not a government agency, registry, bank or regulatory authority. We assist clients with company incorporation and related corporate services through applicable service providers and jurisdictions.",
  },
  service_ui: {
    overview: "Overview", whyConsider: "Why consider it", whatProvides: "What UNICORP provides", howItWorks: "How it works",
    jurisdictions: "Jurisdictions & options", pricingDefaultTitle: "Indicative pricing", addOns: "Optional add-ons",
    faqs: "Frequently asked", refs: "Official references",
    refsNote: "Requirements, fees and categories are set by the relevant authority and should be confirmed against its current official guidance.",
    complianceSensitive: "All services are subject to applicable KYC/AML, regulatory and legal requirements. This is general information based on UNICORP's data and is not legal or tax advice — we make no guarantees of approval or specific outcomes.",
    complianceStandard: "This is general information based on UNICORP's data. Final scope, requirements and pricing are confirmed by our team before you proceed.",
    provides1: "A specialist who owns your case end to end", provides2: "Clear documentation and requirements up front",
    provides3: "Remote onboarding — travel is not usually required", provides4: "Ongoing support after delivery",
    requestQuote: "Request a Quote", related: "Related services",
    readyTitle: "Ready to move forward with {{title}}?", readySub: "Request a tailored quote or talk to a UNICORP specialist.",
    seoTitle: "{{title}} | UNICORP",
  },
  bank_ui: {
    allBanks: "All banks", corporateBanking: "Corporate banking", fintechAccount: "Fintech account", bankAccount: "Bank account",
    currMulti: "Multi-currency (30+ currencies)", timelineDefault: "Timeline confirmed after preliminary review",
    supportFee: "Support fee",
    feeNoteProvider: "Application, KYC/KYB preparation and an introduction. Account approval is decided by the provider.",
    feeNoteBank: "Application, KYC/KYB preparation and an introduction. Account approval is decided by the bank.",
    order: "Order this account", noInc: "No incorporation required", keyFacts: "Key facts",
    factLabel: { fee: "Support fee", type: "Account type", country_count: "Jurisdictions", min_balance: "Min. balance", time_to_open: "Time to open", multi_currency: "Multi-currency", online_banking: "Online banking" },
    type: { Digital: "Digital", Traditional: "Traditional" },
    jurisdictions_one: "{{count}} jurisdiction", jurisdictions_other: "{{count}} jurisdictions",
    about: "About {{name}}", whatsIncluded: "What's included", requirements: "Requirements", howItWorks: "How it works",
    availableIn: "Available for companies in", faqTitle: "Frequently asked questions",
    readyTitle: "Ready to open your {{name}} account?", readySub: "Support fee {{fee}} · no incorporation required.",
    notFound: "Bank not found", browseAll: "Browse all banks",
  },
  country_ui: {
    companyFormation: "Company Formation",
    allCountries: "All countries",
    notFoundTitle: "Country not found",
    browseAll: "Browse all countries",
    setupFee: "Set-up fee",
    customQuotation: "Custom quotation",
    customNote: "Final pricing and billing terms are confirmed before payment.",
    requestQuotation: "Request quotation",
    getAssistance: "Get Incorporation Assistance",
    lastReviewed: "Last reviewed: {{date}}",
    bankOptions_one: "{{count}} banking option",
    bankOptions_other: "{{count}} banking options",
    options_one: "{{count}} option",
    options_other: "{{count}} options",
    frequentlyAdded: "Frequently added with {{name}}",
    bankingAssistance: "Banking application assistance",
    optionalNote: "Optional — added only if you select them during your application.",
    bundleSave: "Add {{bank}} & save 10%",
    bundleDesc: "Bundle a {{type}} account with your company →",
    typeFintech: "fintech",
    typeBank: "bank",
    bankType: { Digital: "Digital", Traditional: "Traditional" },
    whyIncorporate: "Why incorporate in {{name}}?",
    whatsIncluded: "What's included",
    requirements: "Requirements",
    keyFacts: "Key facts",
    keyBenefits: "Key benefits",
    bankingOptionsTitle: "Banking options",
    restrictionsTitle: "Restrictions & notes",
    howItWorks: "How it works",
    bankingIn: "Banking & fintech options in {{name}}",
    bankingBlurb: "Open a corporate account together with your company and save 10% when you bundle.",
    recommended: "Recommended",
    addSave: "Add & save 10%",
    readyToLaunch: "Ready to launch in {{name}}?",
    setupFrom: "Set-up from {{fee}}",
    deliveredIn: "delivered in {{timeline}}",
    faqTitle: "Frequently asked questions",
    factsSuits: "Is this right for you — and what we actually do",
    factsDecisions: "Decisions you'll need to make",
    factsDocuments: "Documents you'll likely need",
    factsIncludes: "What our package includes",
    factsExcludes: "What it does not include",
    factsStages: "How the process runs",
    factsOngoing: "Ongoing obligations",
    factsRefs: "Official references",
    pbService: "Service & registry (all-inclusive)",
    pbTotal: "Total estimated (today)",
    pbAnnualRenewal: "Annual renewal",
    govFeeIncluded: "Government & registry fees included",
    govFeeExcluded: "Government / registry fees billed separately",
    govFeeEstimated: "Government / registry fees estimated — confirmed before payment",
    timelineQualifier: "Estimated processing time starts after complete KYC/document approval.",
    timelineDefault: "Timeline confirmed after preliminary review",
    perYear: "/yr",
    renewalRuleDefault: "Annual renewal billed each year. The renewal date is confirmed at incorporation.",
    factLabel: {
      setup_fee: "Set-up fee",
      annual_fee: "Annual renewal",
      timeline: "Delivery time",
      bank_count: "Banking options",
    },
  },
  seo: {
    homeTitle: "UNICORP | Start, Manage & Grow Your International Company",
    homeDesc: "Company formation, banking assistance, accounting, compliance and corporate services across leading jurisdictions — transparent pricing, remote onboarding, managed end to end.",
    catalogTitle: "Company Formation in Leading Jurisdictions | UNICORP",
    catalogDesc: "Form your company in top global jurisdictions with all-inclusive pricing. Compare set-up fees, timelines and banking options.",
    banksTitle: "Corporate Bank Accounts & EMI | UNICORP",
    banksDesc: "Open a corporate bank or EMI account with end-to-end application support across leading providers.",
    contactTitle: "Contact UNICORP | Global Corporate Services",
    contactDesc: "Talk to our specialists about company formation, banking and compliance.",
  },
};
en.client = enClient.client;
Object.assign(en, enPages);
export default en;
